import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Section, Dropdown, TextField } from 'm2m.components';
import { Grid, Typography } from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import { participanteDocumentLongDescriptions } from 'common/genericDescriptions';
import { participanteUploadDocumentsOptions } from 'common/genericOptions';
import { ParticipanteUploadDocumentType, TipoRepresentanteLegal } from 'common/genericTypes';
import UploadDocs, { AcceptableFileTypes, Attachment } from 'components/uploadDocs';
import { RootState } from 'store';
import { updateDocumentos, updateDocumentosExcluir } from 'store/modules/participantes/registro';
import moment from 'moment';

interface DocumentosProps {
  isStepFulfilled(isFulfilled: boolean): void;
  isDataVencimentoPreenchido(isPreenchido: boolean): void;
  participanteId?: string;
}

const mapStateToProps = (state: RootState) => ({
  participante: state.participante,
});

const mapDispatchToProps = { updateDocumentos, updateDocumentosExcluir };

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & DocumentosProps;

const Documentos: React.FC<Props> = (props) => {
  const {
    participante,
    participante: { representantesLegais, documentos },
    updateDocumentos,
    updateDocumentosExcluir,
    isStepFulfilled,
    isDataVencimentoPreenchido,
  } = props;
  const [tipoDocumento, setTipoDocumento] = useState<string>('');
  
  const documentEleicao = participante.documentos.filter(doc => doc.type === ParticipanteUploadDocumentType.ComprovanteEleicao).at(0);
  const [dataVencimento, setDataVencimento] = useState<string>(documentEleicao?.remoteFileInfo?.dataVencimento ? moment(documentEleicao?.remoteFileInfo?.dataVencimento).format("YYYY-MM-DD") : "");

  useEffect(() => {
    const docs = participanteUploadDocumentsOptions.map((doc) => {
      const documentUploaded = documentos.find((docParticipante) => docParticipante.type === doc.value);
      switch (doc.value) {
        case ParticipanteUploadDocumentType.Procuracao:
          return {
            type: doc.value,
            file: undefined,
            optional: !representantesLegais.some((r) => r.tipo === TipoRepresentanteLegal.Procurador),
            ...documentUploaded,
          };
        case ParticipanteUploadDocumentType.Identidade:
          return {
            type: doc.value,
            file: undefined,
            optional: !representantesLegais.some(
              (r) => r.tipo === TipoRepresentanteLegal.Administrador || r.tipo === TipoRepresentanteLegal.Procurador
            ),
            ...documentUploaded,
          };
        case ParticipanteUploadDocumentType.Outros:
          return { type: doc.value, file: undefined, optional: true, ...documentUploaded };
        default:
          return { type: doc.value, file: undefined, optional: false, ...documentUploaded };
      }
    });
    updateDocumentos(docs);
  }, [representantesLegais, updateDocumentos]);

  const handleUpdateFile = (attachments: Attachment[]) => {
    const newAttachment = attachments?.[0];
    const newDocumentsInfo = participante.documentos.map((documentInfo) => {
      if (documentInfo.type === newAttachment.id) {
        return { ...documentInfo, file: newAttachment.file };
      }
      return { ...documentInfo };
    });
    updateDocumentos(newDocumentsInfo);
  };

  const handleDeleteFile = (type: string) => {
    const documentosParaExcluir = participante.documentos
      .filter(document => document.type === type && !!document.remoteFileInfo)
      .map(document => ({ ...document }));
    
    if (documentosParaExcluir.length > 0) {
      const novosDocumentosExcluir = [...(participante.documentosExcluir || []), ...documentosParaExcluir];
      updateDocumentosExcluir(novosDocumentosExcluir);
    }

    const newDocumentsInfo = participante.documentos.map((documentInfo) => {
      if (documentInfo.type === type) {
        const { file, remoteFileInfo, ...resto } = documentInfo;
        return resto;
      }
      return { ...documentInfo };
    });

    setDataVencimento("");
    updateDocumentos(newDocumentsInfo);
  };

  useEffect(() => {
    isStepFulfilled(documentos.every((doc) => !!doc.optional || !!doc.file || !!doc.remoteFileInfo));
  }, [documentos, isStepFulfilled]);

  useEffect(() => {
    if (props.participanteId)
    {
      if (dataVencimento)
      {
        isDataVencimentoPreenchido(true);
      }
    } else {
      isDataVencimentoPreenchido(true);
    }
  }, [dataVencimento, isDataVencimentoPreenchido]);

  const dataVencimentoHandleChange = (date: Date) => {
    let documentosUpdated = documentos.map((document) => {
      if (document.type === ParticipanteUploadDocumentType.ComprovanteEleicao) {
        document.dataVencimento = moment(date).format();
      }
      return document;
    });
    setDataVencimento(moment(date).format("YYYY-MM-DD"));
    updateDocumentos(documentosUpdated)
  }

  return (
    <>
      <Section title={'Upload de Documentos'}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Dropdown
              label={'Tipo de Documento'}
              required
              options={participanteUploadDocumentsOptions}
              value={tipoDocumento}
              onChange={(value) => setTipoDocumento(value)}
            />
          </Grid>
          <Grid item xs={12}>
            <UploadDocs
              inputAttachments={participante.documentos
                ?.filter((attachment) => !!attachment.remoteFileInfo)
                .map(({ type, remoteFileInfo }) => ({
                  id: type,
                  remoteFileInfo,
                }))}
              fileId={tipoDocumento}
              disabled={!tipoDocumento}
              onChange={handleUpdateFile}
              onDelete={handleDeleteFile}
              overrideAcceptableFileTypes={[
                AcceptableFileTypes.AppPdf,
                AcceptableFileTypes.ImageJpeg,
                AcceptableFileTypes.Zip,
                AcceptableFileTypes.XZipCompressed,
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            {participante.documentos.map((document) =>
              !document.optional || !!document.file ? (
                <React.Fragment key={document.type}>
                  <Grid container spacing={1}>
                    <Grid item xs={2}>
                      { props.participanteId && document.type === ParticipanteUploadDocumentType.ComprovanteEleicao &&
                        <TextField 
                          value={dataVencimento}
                          helperText='Data de Vencimento' 
                          type='date' 
                          onChange={(event) => dataVencimentoHandleChange(event)}
                          required 
                        />
                      }
                    </Grid>
                    <Grid item>
                      <CheckCircle color={document.file || document.remoteFileInfo ? 'primary' : 'disabled'} />
                    </Grid>
                    <Grid item xs>
                      <Typography variant="body2">{participanteDocumentLongDescriptions[document.type]}</Typography>
                    </Grid>
                  </Grid>
                </React.Fragment>
              ) : null
            )}
          </Grid>
        </Grid>
      </Section>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Documentos);